import React from 'react';
import { Card, CardContent, Typography, Box, Divider } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import {
    Book as BookIcon,
    PersonPin as TeacherIcon,
    Schedule as ScheduleIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Phone as PhoneIcon
} from '@mui/icons-material';
import { toPersianDigits } from '../../../../utils/convertNumbers';

const StudentCardBox = styled(Card)(({ theme }) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease',
    cursor: 'pointer',
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.border.main}`,
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
        borderColor: theme.palette.primary.main,
    },
    '&:active': {
        transform: 'translateY(-1px)',
    }
}));

const SideStick = styled('span')(({ theme, status }) => {
    const tone = status === 'negative' ? theme.palette.error.main :
                 status === 'zero' ? theme.palette.warning.main :
                 status === 'positive' ? theme.palette.success.main :
                 theme.palette.primary.main;
    return {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '4px',
        height: '100%',
        background: `linear-gradient(180deg, ${alpha(tone, 0.24)} 0%, ${tone} 100%)`,
        borderRadius: '0 2px 2px 0',
    };
});

const StudentName = styled(Typography)(({ theme }) => ({
    fontWeight: 700,
    fontSize: '1.1rem',
    color: theme.palette.text.default,
    marginBottom: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const StudentInfo = styled(Typography)(({ theme }) => ({
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
}));

const CourseInfo = styled(Typography)(({ theme }) => ({
    fontSize: '0.85rem',
    color: theme.palette.text.default,
    marginBottom: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
}));

const SessionStatusBox = styled(Box)(({ theme, status }) => {
    const palette = status === 'negative' ? theme.palette.error :
                    status === 'zero' ? theme.palette.warning :
                    theme.palette.success;
    return {
        padding: theme.spacing(0.75, 1),
        borderRadius: theme.shape.borderRadius,
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        marginTop: theme.spacing(1),
        backgroundColor: alpha(palette.main, 0.12),
        border: `1px solid ${alpha(palette.dark ?? palette.main, 0.6)}`,
    };
});

const ScheduleInfo = styled(Typography)(({ theme }) => ({
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
}));

const DAY_CHOICES = [
    { value: 0, label: 'شنبه' },
    { value: 1, label: 'یکشنبه' },
    { value: 2, label: 'دوشنبه' },
    { value: 3, label: 'سه‌شنبه' },
    { value: 4, label: 'چهارشنبه' },
    { value: 5, label: 'پنج‌شنبه' },
    { value: 6, label: 'جمعه' },
];

function formatTime(timeString) {
    if (!timeString) return '-';
    return toPersianDigits(timeString.slice(0, 5));
}

function getDayLabel(dayValue) {
    const day = DAY_CHOICES.find(d => d.value === dayValue);
    return day ? day.label : '-';
}

export default function EnrollmentCard({ enrollment, onClick }) {
    const remained = enrollment.remained_sessions;
    const sessionStatus = remained < 0 ? 'negative' : (remained === 0 ? 'zero' : 'positive');
    const studentFullName = `${enrollment.student.first_name} ${enrollment.student.last_name}`.trim();
    const teacherFullName = enrollment.course.teacher
        ? `${enrollment.course.teacher.first_name} ${enrollment.course.teacher.last_name}`.trim()
        : '-';
    const mobile = enrollment.student.mobile || '';

    return (
        <StudentCardBox>
            <SideStick status={sessionStatus} />
            <CardContent sx={{ textAlign: 'right', p: 2.5 }} onClick={onClick}>
                {/* Header: name (removed circular avatar + quick-call icon) */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <StudentName component="div" sx={{ mb: 0 }}>
                        {studentFullName}
                    </StudentName>
                </Box>
                {/* Student Mobile */}
                <StudentInfo>
                    <PhoneIcon sx={{ fontSize: '0.9rem' }} />
                    {mobile ? toPersianDigits(mobile) : '-'}
                </StudentInfo>
                
                <Divider sx={{ my: 1.5, opacity: 0.12 }} />
                
                {/* Course Name */}
                <CourseInfo>
                    <BookIcon sx={{ fontSize: '0.9rem', color: 'primary.main' }} />
                    نام دوره: {enrollment.course.course_name}
                </CourseInfo>
                
                {/* Teacher Name */}
                <CourseInfo>
                    <TeacherIcon sx={{ fontSize: '0.9rem', color: 'primary.main' }} />
                    مدرس: {teacherFullName}
                </CourseInfo>
                
                {/* Session Status */}
                <SessionStatusBox status={sessionStatus}>
                    {sessionStatus === 'negative' && <CancelIcon sx={{ fontSize: '0.9rem', color: 'error.main' }} />}
                    {sessionStatus === 'zero' && <WarningIcon sx={{ fontSize: '0.9rem', color: 'warning.main' }} />}
                    {sessionStatus === 'positive' && <CheckCircleIcon sx={{ fontSize: '0.9rem', color: 'success.main' }} />}
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                        جلسات باقی مانده: {remained !== null && remained !== undefined ? toPersianDigits(String(remained)) : '-'}
                    </Typography>
                </SessionStatusBox>
                
                {/* Schedule Info */}
                <ScheduleInfo>
                    <ScheduleIcon sx={{ fontSize: '0.8rem' }} />
                    روز و ساعت کلاس: {getDayLabel(enrollment.day)} {formatTime(enrollment.start_time)}
                </ScheduleInfo>
            </CardContent>
        </StudentCardBox>
    );
}

